import { CoralHandler, CoralRequest, CoralResponse } from '@amzn/ihm-js-coral-client'
import { Observable } from 'rxjs'
import { executeRequest } from '@coral/CoralRequestExecutor'
import { LatencyMetric, CountMetric } from '@amzn/ihm-associate-tools-js-lib'
import { getStoreId } from '@utils/EnvironmentUtil'
import { getOrInitRumClient } from '@amzn/jwo-store-management-platform-components'
import type { TokenRefreshResult } from './TokenProvider'

/**
 * Records a CloudWatch RUM custom event naming the gateway operation that failed.
 *
 * WHY: RUM's built-in `http` telemetry emits `http_event` with only method + URL +
 * status. Every gateway call is a POST to the same bare host with the operation in
 * the `X-Amz-Target` header, which RUM does not capture — so a 5xx spike in the
 * `Http5xxErrors` alarm can't be attributed to a specific API without deep-diving
 * gateway server logs. This event surfaces the operation directly alongside the
 * `http_event` in the same session.
 *
 * SECURITY (ARCC SAX-06 — Sensitive Data Protection in Logs): we record ONLY the
 * Coral operation name (the `X-Amz-Target` value, e.g. `GetCurrentUser`) and the
 * numeric status. We deliberately do NOT serialize `request.headers` / the header
 * bag — that array carries the `Authorization: Bearer` token (pushed in below) and
 * `X-Client-Id`, and logging headers/tokens is a named SAX-06 pitfall. The operation
 * name is a non-sensitive, hardcoded model identifier.
 */
function recordGatewayError (operationName: string, status: number | undefined): void {
    try {
        const rumClient = getOrInitRumClient()
        if (!rumClient) {
            return
        }
        rumClient.recordEvent('gateway_request_error', {
            operation: operationName,
            // 0 = transport/network failure (no HTTP response received).
            status: typeof status === 'number' ? status : 0,
        })
    } catch {
        // Never let telemetry break the request path.
    }
}

/**
 * How the handler recovers when the gateway rejects the access token with a 401.
 */
export interface IUnauthorized401Recovery {
    /** Refresh the access token now. */
    refreshToken: () => Promise<TokenRefreshResult>
    /** Called when the refresh fails — expected to end the session and send the user to sign in. */
    onRefreshFailed: () => void
}

/** Sets the bearer token, replacing any earlier one so a retried request does not carry two. */
function setAuthorizationHeader (request: CoralRequest, accessToken: string): void {
    // findIndex returns -1 when there is no Authorization header yet; otherwise remove that one entry.
    const authorizationHeaderIndex = request.headers.findIndex(([name]) => name === 'Authorization')
    if (authorizationHeaderIndex !== -1) {
        request.headers.splice(authorizationHeaderIndex, 1)
    }
    request.headers.push(['Authorization', `Bearer ${accessToken}`])
}

/**
 * Coral handler that adds authentication headers to requests.
 * Injects Bearer token and X-Client-Id for tenant resolution.
 * Emits gateway API latency and fault metrics.
 */
export class AuthCoralHandler implements CoralHandler {
    constructor (
        private getAccessToken: () => Promise<string>,
        private getClientId?: () => string | null,
        private unauthorized401Recovery?: IUnauthorized401Recovery,
    ) {}

    public handle (request: CoralRequest): Observable<CoralResponse<any>> {
        return new Observable((observer) => {
            const abortController = new AbortController()
            const operationName = request.operation || 'unknown'
            const latency = new LatencyMetric(getStoreId, 'Time', 'JWOStoreManagementPlatform', `Gateway.${operationName}.Latency`)
            const aggregateLatency = new LatencyMetric(getStoreId, 'Time', 'JWOStoreManagementPlatform', 'Gateway.Latency')
            console.log('[SMP] AuthCoralHandler: Getting access token')
            let requestStarted = false
            // Gateway latency covers one request. It stops when the first response arrives, so a 401's token
            // refresh and retried request are not counted here — they are timed separately under
            // Gateway.401_UnauthorizedRefresh.Latency and Gateway.401_UnauthorizedRetry.Latency.
            let latencyStopped = false
            const stopLatency = (): void => {
                if (latencyStopped) return
                latencyStopped = true
                latency.stop()
                aggregateLatency.stop()
            }
            this.getAccessToken()
                .then(async (accessToken) => {
                    console.log('[SMP] AuthCoralHandler: Auth header added')
                    setAuthorizationHeader(request, accessToken)
                    const clientId = this.getClientId?.() ?? null
                    if (clientId) {
                        request.headers.push(['X-Client-Id', clientId])
                    }
                    requestStarted = true
                    latency.start()
                    aggregateLatency.start()
                    const response = await executeRequest(request, abortController.signal)
                    stopLatency()
                    return this.retryOn401Unauthorized(request, response, abortController.signal, accessToken)
                })
                .then((response) => {
                    // A gateway 5xx resolves here as a CoralResponse with ok=false (the
                    // executor does not throw on non-2xx), so name the failing operation
                    // for RUM. Scope to status>=500 only: 4xx are client errors (e.g. a
                    // modelled 403 authz denial) and must not count as a gateway fault.
                    const isServerFault = typeof response?.status === 'number' && response.status >= 500
                    new CountMetric(getStoreId(), 'Fault', 'JWOStoreManagementPlatform', `Gateway.${operationName}.Fault`).count(isServerFault ? 1 : 0)
                    new CountMetric(getStoreId(), 'Fault', 'JWOStoreManagementPlatform', 'Gateway.Fault').count(isServerFault ? 1 : 0)
                    if (isServerFault) {
                        recordGatewayError(operationName, response?.status)
                    }
                    requestStarted = false
                    observer.next(response)
                    observer.complete()
                })
                .catch((error) => {
                    if (requestStarted && error?.name !== 'AbortError') {
                        stopLatency()
                        new CountMetric(getStoreId(), 'Fault', 'JWOStoreManagementPlatform', `Gateway.${operationName}.Fault`).count(1)
                        new CountMetric(getStoreId(), 'Fault', 'JWOStoreManagementPlatform', 'Gateway.Fault').count(1)
                        // Transport/network failure — no HTTP response, so status is unknown.
                        recordGatewayError(operationName, undefined)
                    }
                    observer.error(error)
                })

            // Teardown: cancel request if unsubscribed
            return () => abortController.abort()
        })
    }

    /**
     * A 401 means the gateway rejected the access token itself — typically expired, even when the local
     * expiry says otherwise. Refresh once and retry the request once with the new token. If the session cannot
     * be refreshed, or the retried request is rejected again with the new token, the session is over: hand off to
     * onRefreshFailed rather than letting every later call fail (or refresh) again. A second 401 is returned
     * as-is, so this cannot loop.
     * Only 401 is handled: a 403 is an authorization decision (e.g. expired time-bound access) that a new
     * token would not change.
     */
    private async retryOn401Unauthorized (
        request: CoralRequest,
        response: CoralResponse<any>,
        signal: AbortSignal,
        sentAccessToken: string,
    ): Promise<CoralResponse<any>> {
        if (response?.status !== 401 || !this.unauthorized401Recovery) {
            return response
        }
        // Concurrent requests share a refresh only while it is in flight. If another request's refresh finished
        // while this one was waiting on its 401, a newer token is already stored — retry with it rather than
        // refreshing again. A second 401 here does not end the session: whoever refreshed that token handles
        // its own rejection, and on React Native the token comes from the native shell, which owns the session.
        const latestAccessToken = await this.getAccessToken().catch((): string | null => null)
        if (latestAccessToken && latestAccessToken !== sentAccessToken) {
            return this.retryWithToken(request, signal, latestAccessToken, false)
        }
        const refreshStartedAt = Date.now()
        // An unexpected error is not evidence that the session is over, so it is treated like a temporary failure
        // rather than signing the user out.
        const result = await this.unauthorized401Recovery.refreshToken()
            .catch((): TokenRefreshResult => ({ kind: 'transient' }))
        if (result.kind === 'unsupported') {
            // The platform cannot refresh on demand — the React Native app, where the native shell owns the
            // Cognito session and refreshes the token itself. Signing out here would log the user out on any
            // single 401, including one the native side has already recovered from, so the 401 is passed
            // through unchanged, as it was before this recovery existed. Nothing was refreshed, so no refresh
            // latency is recorded.
            return response
        }
        new LatencyMetric(getStoreId, 'Time', 'JWOStoreManagementPlatform', 'Gateway.401_UnauthorizedRefresh.Latency')
            .setValueAndBroadcast(Date.now() - refreshStartedAt)
        if (result.kind === 'transient') {
            // The token endpoint could not be reached or had an error of its own; the session itself may be
            // fine. Keep it and pass the 401 through — the next 401 tries the refresh again.
            return response
        }
        if (result.kind === 'failed') {
            this.unauthorized401Recovery.onRefreshFailed()
            return response
        }
        return this.retryWithToken(request, signal, result.accessToken, true)
    }

    /** Retries the request once with a new token; a second 401 ends the session when endSessionOn401 is set. */
    private async retryWithToken (
        request: CoralRequest,
        signal: AbortSignal,
        accessToken: string,
        endSessionOn401: boolean,
    ): Promise<CoralResponse<any>> {
        new CountMetric(getStoreId(), 'Count', 'JWOStoreManagementPlatform', 'Gateway.401_UnauthorizedRetry').count(1)
        setAuthorizationHeader(request, accessToken)
        // Times only the retried request; the refresh before it is timed by Gateway.401_UnauthorizedRefresh.Latency.
        const retryLatency = new LatencyMetric(getStoreId, 'Time', 'JWOStoreManagementPlatform', 'Gateway.401_UnauthorizedRetry.Latency')
        retryLatency.start()
        let retried: CoralResponse<any>
        try {
            retried = await executeRequest(request, signal)
        } finally {
            retryLatency.stop()
        }
        if (retried?.status === 401 && endSessionOn401) {
            // The gateway rejected a token that was just issued, so refreshing again cannot help — and without
            // this, every later call would repeat the refresh and retry. Treat it like a failed refresh.
            this.unauthorized401Recovery.onRefreshFailed()
        }
        return retried
    }
}
